/**
 * Spatial geometry helpers for polygons.
 *
 * Pure functions — no SDK coupling, no DOM, no side effects.
 *
 * @module searchResultsMap/services/geometry
 */

/**
 * Tests if a polygon's bounding box intersects the bounds provided.
 * Uses AABB (axis-aligned bounding box) overlap test.
 * Returns true when bounds is null/undefined.
 *
 * @param {Object} polygon - Polygon with topRightLatitude/Longitude, bottomLeftLatitude/Longitude
 * @param {{ north: number, south: number, east: number, west: number }|null} bounds
 * @returns {boolean}
 */
export function isPolygonInBounds(polygon, bounds) {
  if (!bounds) {
    return true;
  }
  return (
    polygon.topRightLatitude    >= bounds.south &&
    polygon.bottomLeftLatitude  <= bounds.north &&
    polygon.topRightLongitude   >= bounds.west  &&
    polygon.bottomLeftLongitude <= bounds.east
  );
}

/**
 * Tests if a point intersects the bounds provided.
 * Uses AABB (axis-aligned bounding box) overlap test.
 * Returns true when bounds is null/undefined.
 *
 * @param {Object} point - Point with latitude and longitude
 * @param {{ north: number, south: number, east: number, west: number }|null} bounds
 * @returns {boolean}
 */
export function isPointInBounds(point, bounds) {
  if (!bounds) {
    return true;
  }

  return (
    point.lat  >= bounds.south &&
    point.lat  <= bounds.north &&
    point.lng  >= bounds.west &&
    point.lng  <= bounds.east
  );
}

/**
 * Merges two sets of coordinate bounds by taking the union of both rectangles.
 * The result encompasses both the base bounds and the extension bounds.
 *
 * @param {{ minLatitude: number, minLongitude: number, maxLatitude: number, maxLongitude: number }} baseBounds
 * @param {{ minLatitude: number, minLongitude: number, maxLatitude: number, maxLongitude: number }} extensionBounds
 * @returns {{ minLatitude: number, minLongitude: number, maxLatitude: number, maxLongitude: number }}
 */
export function mergeBounds(baseBounds, extensionBounds) {
  return {
    minLatitude: Math.min(baseBounds.minLatitude, extensionBounds.minLatitude),
    maxLatitude: Math.max(baseBounds.maxLatitude, extensionBounds.maxLatitude),
    minLongitude: Math.min(baseBounds.minLongitude, extensionBounds.minLongitude),
    maxLongitude: Math.max(baseBounds.maxLongitude, extensionBounds.maxLongitude),
  };
}

/**
 * Returns true when a camera snapshot differs from a previously recorded one.
 * Compares zoom and all four bound edges with strict equality.
 * Returns true when either snapshot is absent so the first render is never skipped.
 *
 * @param {{ zoom: number, bounds: { north: number, south: number, east: number, west: number }|null }|null} previous
 * @param {{ zoom: number, bounds: { north: number, south: number, east: number, west: number }|null }|null} current
 * @returns {boolean}
 */
export function hasCameraChanged(previousCamera, currentCamera) {
  if (!previousCamera || !currentCamera) {
    return true;
  }

  if (previousCamera.zoom !== currentCamera.zoom) {
    return true;
  }
  
  const previousBounds = previousCamera.bounds;
  const currentBounds = currentCamera.bounds;

  if (!previousBounds && !currentBounds) {
    return false;
  }

  if (!previousBounds || !currentBounds) {
    return true;
  }

  return (
    previousBounds.north !== currentBounds.north ||
    previousBounds.south !== currentBounds.south ||
    previousBounds.east !== currentBounds.east ||
    previousBounds.west !== currentBounds.west
  );
}